import { t } from '../../context/LocaleContext';
import { getLocalizedName } from '../../utils/i18n';
import React, { useEffect, useState } from 'react';
import { Statistic, Row, Col, Spin, DatePicker, Select, Button, Table, Space, message } from 'antd';
import { DollarOutlined, FileTextOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../../api';
import dayjs from 'dayjs';
import { useLocale } from '../../context/LocaleContext';
import PageHeader from '../../components/PageHeader';
import DataCard from '../../components/DataCard';
import { formatMoney } from '../../utils/format';

const { RangePicker } = DatePicker;

interface SalesReportItem {
  period: string;
  order_count: number;
  total_sales: number;
  total_profit: number;
}

interface SalesStats {
  total_sales: number;
  total_orders: number;
  total_profit: number;
  avg_order_value: number;
  overall_margin: number;
}

const groupByOptions = [
  { value: 'day', label: t('按天') },
  { value: 'week', label: t('按周') },
  { value: 'month', label: t('按月') },
  { value: 'salesperson', label: t('按业务员') },
  { value: 'store', label: t('按门店') },
];

// NOTE: labels are translated at usage site with t()

const SalesReport: React.FC = () => {
  const { locale } = useLocale();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SalesReportItem[]>([]);
  const [stats, setStats] = useState<SalesStats | null>(null);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([dayjs().subtract(30, 'day'), dayjs()]);
  const [groupBy, setGroupBy] = useState('day');
  const [storeId, setStoreId] = useState<number | undefined>();
  const [salespersonId, setSalespersonId] = useState<number | undefined>();
  const [stores, setStores] = useState<{ id: number; name: string }[]>([]);
  const [salespeople, setSalespeople] = useState<{ id: number; real_name: string }[]>([]);

  useEffect(() => {
    // 加载门店和业务员选项
    api.get('/stores', { params: { page_size: 100 } }).then((res: any) => {
      if (res.code === 0) setStores(res.data.items || []);
    });
    api.get('/users', { params: { role: 'salesperson', page_size: 100 } }).then((res: any) => {
      if (res.code === 0) setSalespeople(res.data.items || []);
    });
  }, []);

  const fetchReport = () => {
    setLoading(true);
    const params: any = {
      date_from: dateRange[0].format('YYYY-MM-DD'),
      date_to: dateRange[1].format('YYYY-MM-DD'),
      group_by: groupBy,
    };
    if (storeId) params.store_id = storeId;
    if (salespersonId) params.salesperson_id = salespersonId;

    api.get('/reports/sales', { params })
      .then((res: any) => {
        if (res.code === 0) {
          setData(res.data.items || []);
          setStats(res.data.stats || null);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const handleExport = () => {
    message.success(t('报表导出成功'));
  };

  const columns = [
    {
      title: t(groupByOptions.find(o => o.value === groupBy)?.label || t('时间/维度')),
      dataIndex: 'period',
      key: 'period',
    },
    {
      title: t('订单数'),
      dataIndex: 'order_count',
      key: 'order_count',
      align: 'right' as const,
    },
    {
      title: t('销售额 (USD)'),
      dataIndex: 'total_sales',
      key: 'total_sales',
      align: 'right' as const,
      render: (v: number) => `$${v?.toFixed?.(2) ?? v}`,
    },
    {
      title: t('毛利 (USD)'),
      dataIndex: 'total_profit',
      key: 'total_profit',
      align: 'right' as const,
      render: (v: number) => <span style={{ color: '#52c41a' }}>${v?.toFixed?.(2) ?? v}</span>,
    },
    {
      title: t('毛利率'),
      dataIndex: 'margin_rate',
      key: 'margin_rate',
      align: 'right' as const,
      render: (_: any, record: SalesReportItem) => {
        const rate = record.total_sales > 0 ? (record.total_profit / record.total_sales * 100).toFixed(1) : '0.0';
        return `${rate}%`;
      },
    },
  ];

  return (
    <div>
      <PageHeader title={t('销售报表')} subtitle={t('查看销售数据统计及趋势分析')} />

      <div className="jds-filter-bar">
        <Space wrap>
          <RangePicker
            value={dateRange}
            onChange={(dates) => dates && setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
          />
          <Select
            value={groupBy}
            onChange={setGroupBy}
            options={groupByOptions.map(opt => ({ ...opt, label: t(opt.label) }))}
            style={{ width: 120 }}
          />
          <Select
            placeholder={t("选择门店")}
            allowClear
            value={storeId}
            onChange={setStoreId}
            options={stores.map(s => ({ value: s.id, label: getLocalizedName(s, locale) }))}
            style={{ width: 160 }}
          />
          {groupBy !== 'salesperson' && (
            <Select
              placeholder={t("选择业务员")}
              allowClear
              value={salespersonId}
              onChange={setSalespersonId}
              options={salespeople.map(s => ({ value: s.id, label: s.real_name }))}
              style={{ width: 130 }}
            />
          )}
          <Button type="primary" icon={<ReloadOutlined />} onClick={fetchReport}>{t('查询')}</Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>{t('导出')}</Button>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <DataCard>
            <Statistic
              title={t("销售总额 (USD)")}
              value={stats?.total_sales || 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1a5fb4' }}
              precision={2}
            />
          </DataCard>
        </Col>
        <Col span={6}>
          <DataCard>
            <Statistic
              title={t("订单总数")}
              value={stats?.total_orders || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </DataCard>
        </Col>
        <Col span={6}>
          <DataCard>
            <Statistic
              title={t("平均客单价")}
              value={stats?.avg_order_value || 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#faad14' }}
              precision={2}
            />
          </DataCard>
        </Col>
        <Col span={6}>
          <DataCard>
            <Statistic
              title={t("总毛利率")}
              value={stats?.overall_margin || 0}
              suffix="%"
              valueStyle={{ color: '#52c41a' }}
              precision={1}
            />
          </DataCard>
        </Col>
      </Row>

      <DataCard title={t("销售明细")} bodyStyle={{ padding: 0 }}>
        <div style={{ padding: '0 20px 20px' }}>
          <Table
            dataSource={data}
            columns={columns}
            rowKey="period"
            loading={loading}
            pagination={{ pageSize: 20 }}
            summary={(pageData) => {
              if (!pageData.length) return null;
              const totalOrders = pageData.reduce((sum, item) => sum + item.order_count, 0);
              const totalSales = pageData.reduce((sum, item) => sum + item.total_sales, 0);
              const totalProfit = pageData.reduce((sum, item) => sum + item.total_profit, 0);
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}><b>{t("合计")}</b></Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right"><b>{totalOrders}</b></Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right"><b>{formatMoney(totalSales)}</b></Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right"><b style={{ color: '#52c41a' }}>{formatMoney(totalProfit)}</b></Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <b>{totalSales > 0 ? (totalProfit / totalSales * 100).toFixed(1) : '0.0'}%</b>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </div>
      </DataCard>
    </div>
  );
};
export default SalesReport;
